/**
 * Contorno na câmera de quem está falando.
 * O RealtimeKit emite `activeSpeaker` com o peer id quando a voz muda de pessoa.
 * O tile (`rtk-participant-tile`) guarda esse id em `.participant.id`.
 *
 * O tile vive dentro de shadow roots do kit: CSS global não o alcança, e o
 * vídeo cobre qualquer borda/sombra do próprio host. Por isso o contorno é um
 * ::after injetado no shadow root do tile, acima do vídeo.
 */

export const SPEAKING_ATTR = "data-constela-speaking";

const STYLE_ATTR = "data-constela-speaking-style";

export const SPEAKING_TILE_CSS = `
:host([${SPEAKING_ATTR}="true"])::after {
  content: "";
  position: absolute;
  inset: 0;
  border: 4px solid #ffffff;
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.35), inset 0 0 14px rgba(255, 255, 255, 0.45);
  pointer-events: none;
  z-index: 50;
}
`;

const TILE_TAGS = new Set(["rtk-participant-tile", "dyte-participant-tile"]);

type TileElement = HTMLElement & { participant?: { id?: string } };

export function tilePeerId(el: HTMLElement): string {
  const id = (el as TileElement).participant?.id;
  return typeof id === "string" ? id : "";
}

function ensureTileStyle(el: HTMLElement): void {
  const shadow = el.shadowRoot;
  if (!shadow || shadow.querySelector(`style[${STYLE_ATTR}]`)) return;
  const style = document.createElement("style");
  style.setAttribute(STYLE_ATTR, "true");
  style.textContent = SPEAKING_TILE_CSS;
  shadow.appendChild(style);
}

function walkTree(root: ParentNode, onElement: (el: HTMLElement) => void, onShadow?: (s: ShadowRoot) => void): void {
  const walk = (node: ParentNode) => {
    const elements =
      node instanceof Element
        ? [node, ...Array.from(node.querySelectorAll("*"))]
        : Array.from(node.querySelectorAll("*"));
    for (const el of elements) {
      if (el instanceof HTMLElement) onElement(el);
      const shadow = (el as HTMLElement).shadowRoot;
      if (shadow) {
        onShadow?.(shadow);
        walk(shadow);
      }
    }
  };
  walk(root);
}

export function markSpeakingTiles(
  root: ParentNode,
  peerId: string,
  onShadow?: (s: ShadowRoot) => void,
): number {
  let marked = 0;
  walkTree(
    root,
    (el) => {
      if (!TILE_TAGS.has(el.tagName.toLowerCase())) return;
      ensureTileStyle(el);
      const speaking = peerId.length > 0 && tilePeerId(el) === peerId;
      if (speaking) {
        if (el.getAttribute(SPEAKING_ATTR) !== "true") el.setAttribute(SPEAKING_ATTR, "true");
        marked += 1;
      } else if (el.hasAttribute(SPEAKING_ATTR)) {
        el.removeAttribute(SPEAKING_ATTR);
      }
    },
    onShadow,
  );
  return marked;
}

type SpeakingMeeting = {
  participants: {
    lastActiveSpeaker?: string;
    on: (event: "activeSpeaker", handler: (payload: { peerId?: string }) => void) => void;
    removeListener: (event: "activeSpeaker", handler: (payload: { peerId?: string }) => void) => void;
  };
};

/**
 * Liga o contorno e observa tiles novos (paginação, alguém que entra).
 * O SDK só avisa quando a pessoa da vez muda, então o contorno fica
 * até outra voz assumir — o mesmo comportamento do Meet.
 */
export function enableSpeakingOutline(
  rootEl: HTMLElement | null,
  meeting: SpeakingMeeting | null,
): () => void {
  if (!rootEl || !meeting || typeof MutationObserver === "undefined") {
    return () => undefined;
  }

  let peerId = meeting.participants.lastActiveSpeaker || "";
  let timer: number | undefined;
  const observedShadows = new WeakSet<ShadowRoot>();
  const observers: MutationObserver[] = [];

  const watchShadow = (shadow: ShadowRoot) => {
    if (observedShadows.has(shadow)) return;
    observedShadows.add(shadow);
    const observer = new MutationObserver(schedule);
    observer.observe(shadow, { childList: true, subtree: true });
    observers.push(observer);
  };

  const apply = () => {
    timer = undefined;
    markSpeakingTiles(rootEl, peerId, watchShadow);
  };

  // Mutations do kit chegam em rajadas; um passe a cada 250ms basta.
  function schedule() {
    if (timer !== undefined) return;
    timer = window.setTimeout(apply, 250);
  }

  const onActiveSpeaker = (payload: { peerId?: string }) => {
    if (!payload?.peerId || payload.peerId === peerId) return;
    peerId = payload.peerId;
    window.requestAnimationFrame(apply);
  };

  meeting.participants.on("activeSpeaker", onActiveSpeaker);
  const rootObserver = new MutationObserver(schedule);
  rootObserver.observe(rootEl, { childList: true, subtree: true });
  observers.push(rootObserver);
  const timers = [100, 400, 1200, 3000].map((ms) => window.setTimeout(apply, ms));
  apply();

  return () => {
    meeting.participants.removeListener("activeSpeaker", onActiveSpeaker);
    observers.forEach((observer) => observer.disconnect());
    timers.forEach((id) => window.clearTimeout(id));
    if (timer !== undefined) window.clearTimeout(timer);
    markSpeakingTiles(rootEl, "");
  };
}
